import { svgPathProperties } from 'svg-path-properties'
import Text from '~/components/Text/Text'
import { LinkExtraProps, NodeExtraProps, SaFData } from './SaF.types'
import { sankeyLinkHorizontal, SankeyNode } from 'd3-sankey'
import { LinkLabel } from './SaF.styles'
import { useMemo } from 'react'

type UseSaFLinksProps = {
  links: SaFData['links']
}

export default function useSaFLinks({ links }: UseSaFLinksProps) {
  const linksPathData = useMemo(
    () =>
      links.flatMap((link) => {
        const linkGenerator = sankeyLinkHorizontal()
        const d = linkGenerator(link)

        if (!d) {
          return []
        }

        const properties = new svgPathProperties(d)

        const middlePoint = properties.getPointAtLength(
          properties.getTotalLength() / 2
        )

        const source = link.source as SankeyNode<NodeExtraProps, LinkExtraProps>
        const target = link.target as SankeyNode<NodeExtraProps, LinkExtraProps>

        const maxWidth = (target.x0 || 0) - (source.x1 || 0) - 16

        return {
          d,
          middlePoint,
          link,
          maxWidth
        }
      }),
    [links]
  )

  const linkPaths = useMemo(
    () =>
      linksPathData.map(({ d, link }, index) => (
        <path
          key={index}
          d={d}
          stroke='#C6D3DE'
          fill='none'
          strokeWidth={link.width}
        />
      )),
    [linksPathData]
  )

  const linkLabels = useMemo(
    () =>
      linksPathData.flatMap(({ middlePoint, maxWidth, link }) =>
        link.label ? (
          <LinkLabel
            key={`${middlePoint.x}-${middlePoint.y}`}
            style={{
              top: middlePoint.y,
              left: middlePoint.x,
              maxWidth
            }}
          >
            <Text variant='content-02'>{link.label}</Text>
          </LinkLabel>
        ) : (
          []
        )
      ),
    [linksPathData]
  )

  return {
    linkPaths,
    linkLabels
  }
}
